<script setup>
import { ref } from 'vue'
import BcsDropdown from '../../BcsDropdown.vue'
import AlertNotification from '../../AlertNotification.vue'
import BcsModal from '../../BcsModal.vue'
import DatePicker from '../../DatePicker.vue'
import { formatMMMDYYYY } from '../helpers'
import { subtractBusinessDays, calculateDaysDifference } from '../../Reminders/dateUtils.js'

const props = defineProps({
  internalRequirements: { type: Array, required: true },
  allSelected: { type: Boolean, required: true },
  selectedSummary: { type: Object, required: true },
  canShowSummary: { type: Boolean, required: true },
  sortDirection: { type: String, default: 'asc' }
})

const emit = defineEmits([
  'toggle-select-all',
  'toggle-requirement',
  'toggle-employers-expand',
  'toggle-employer-selection',
  'requirement-clicked',
  'business-days-change',
  'toggle-sort'
])

// Custom date modal state
const customDateModalRef = ref(null)
const selectedReqForCustomDate = ref(null)
const selectedCustomDate = ref(null)

function onSelectAll(e){ emit('toggle-select-all', e) }
function onToggleRequirement(req){ emit('toggle-requirement', req) }
function onToggleExpand(req){ emit('toggle-employers-expand', req) }
function onToggleEmployer(req, emp){ emit('toggle-employer-selection', { req, emp }) }
function onReqClicked(req){ emit('requirement-clicked', req) }

function onBusinessDaysChange(opt, req) {
  // If "Custom date" is selected, open the modal
  if (opt.value === -1) {
    selectedReqForCustomDate.value = req
    selectedCustomDate.value = null
    customDateModalRef.value?.openModal()
    return
  }
  emit('business-days-change', { opt, req })
}

function onCustomDateConfirm() {
  if (!selectedCustomDate.value || !selectedReqForCustomDate.value) return
  
  const req = selectedReqForCustomDate.value
  const dueDate = req.earliestDueDateObj
  const reminderDate = new Date(selectedCustomDate.value)
  const businessDays = calculateDaysDifference(dueDate, reminderDate)
  
  // Emit with custom date info
  emit('business-days-change', {
    opt: { value: -2, isCustom: true, customDays: businessDays, customDate: selectedCustomDate.value },
    req
  })
  
  customDateModalRef.value?.closeModal()
  selectedReqForCustomDate.value = null
  selectedCustomDate.value = null
}

function onCustomDateCancel() {
  if (selectedReqForCustomDate.value) {
    selectedReqForCustomDate.value.reminderBusinessDays = null
  }
  customDateModalRef.value?.closeModal()
  selectedReqForCustomDate.value = null
  selectedCustomDate.value = null
}

// Reminder options builder
const baseBusinessDayOptions = [0,1,3,5,10]
function buildReminderOptions(req) {
  const today = new Date()
  today.setHours(0,0,0,0)
  
  const options = baseBusinessDayOptions.map(val => ({
    value: val,
    text: val === 0 ? 'Today' : `${val} day${val>1?'s':''} before`,
    isDisabled: () => {
      if (!req.earliestDueDateObj) return true
      if (val === 0) return false
      const d = subtractBusinessDays(req.earliestDueDateObj, val)
      if (!d) return true
      d.setHours(0,0,0,0)
      return d < today
    }
  }))
  
  // Add custom option if already selected for this requirement
  if (req.customReminderDays != null && req.reminderBusinessDays === -2) {
    const customDays = req.customReminderDays
    const customText = customDays === 1 
      ? 'Custom: 1 day before' 
      : `Custom: ${customDays} days before`
    options.push({
      value: -2,
      text: customText,
      isCustom: true
    })
  }
  
  // Always add "Custom date" option at the end
  options.push({
    value: -1,
    text: 'Custom date'
  })
  
  return options
}

// Format due date for display (e.g. "March 1, 2026" for 2/29 in non-leap years)
function formatDueDateForModal(req) {
  if (!req.earliestDueDateObj) return '--'
  return formatMMMDYYYY(req.earliestDueDateObj)
}
</script>

<template>
  <div id="step1RemindersContainer">
    <div class="table-responsive">
      <table class="table table-sm align-middle" id="sendRemindersRequirementsTable">
        <thead>
          <tr class="table-light">
            <th style="width:43px;" class="text-end align-middle py-2.5 px-0">
              <input id="step1SelectAllRequirements" class="form-check-input" type="checkbox" :checked="allSelected" @change="onSelectAll">
            </th>
            <th class="py-2.5 text-16 text-secondary ps-2 pe-3.5 align-middle"><strong>Requirement</strong></th>
            <th style="width:21%" class="py-2.5 text-16 text-secondary px-3.5 align-middle"><strong>Employers</strong></th>
            <th style="width:13%" class="py-2.5 text-16 text-secondary px-3.5 align-middle">
              <button
                class="btn btn-link text-decoration-none text-16 p-0"
                type="button"
                @click="emit('toggle-sort')"
              >
                <strong>Due Date</strong>
                <i class="bi ms-1" :class="{
                  'bi-sort-down': sortDirection === 'asc',
                  'bi-sort-up': sortDirection === 'desc'
                }"></i>
              </button>
            </th>
            <th style="width:20%" class="py-2.5 text-16 text-secondary px-3.5 align-middle"><strong>Reminder (business days)</strong></th>
            <th style="width:17%" class="py-2.5 text-16 text-secondary px-3.5 align-middle"><strong>Reminder Date</strong></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="req in internalRequirements" :key="req.key" :class="{ 'bg-gray-100': req.expanded }">
            <td class="text-end align-text-top py-1.5 px-0 cell-checkbox">
              <input :id="`step1RequirementCheckbox-${req.key}`" class="form-check-input" type="checkbox" :checked="req.selected" @change="onToggleRequirement(req)">
            </td>
            <td class="align-text-top py-1.5 px-4">
              <a :id="`step1RequirementLink-${req.key}`" href="#" class="text-primary text-decoration-none text-16" @click.prevent="onReqClicked(req)">
                <strong>{{ req.requirementName }}</strong>
              </a>
            </td>
            <td class="align-text-top py-1.5 px-3.5">
              <button :id="`step1ExpandEmployersBtn-${req.key}`" type="button" class="btn btn-link p-0 text-decoration-none text-primary text-14 d-inline-flex align-items-center gap-1" @click="onToggleExpand(req)">
                <strong>{{ req.selectedEmployerKeys.size }} Employer{{ req.selectedEmployerKeys.size !== 1 ? 's' : '' }} Selected</strong>
                <i class="bi" :class="req.expanded ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
              </button>
              <div v-if="req.expanded" class="mt-2">
                <div v-for="emp in req.employers" :key="emp.plan_year_id" class="form-check mb-1">
                  <input class="form-check-input me-2" type="checkbox" :id="`emp-${req.key}-${emp.plan_year_id}`" :checked="req.selectedEmployerKeys.has(emp.plan_year_id)" @change="onToggleEmployer(req, emp)">
                  <label class="form-check-label text-16" :for="`emp-${req.key}-${emp.plan_year_id}`" :class="{ 'text-muted': !req.selectedEmployerKeys.has(emp.plan_year_id)}">
                    {{ emp.employerName }}
                  </label>
                </div>
              </div>
            </td>
            <td class="text-nowrap align-text-top py-1.5 px-3.5 text-14"><strong>{{ formatDueDateForModal(req) }}</strong></td>
            <td class="align-text-top py-1.5 px-3.5">
              <BcsDropdown
                :dropdown-options="buildReminderOptions(req)"
                :context="req"
                v-model="req.reminderBusinessDays"
                placeholder="Please select"
                :teleport="true"
                @change="(opt)=>onBusinessDaysChange(opt, req)" />
            </td>
            <td class="align-text-top py-1.5 px-4 text-16" :class="{'text-muted': !req.reminderDateObj}">
              {{ req.reminderDateStr || '--' }}
            </td>
          </tr>
          <tr v-if="internalRequirements.length === 0">
            <td colspan="6" class="text-center py-4 text-gray-600">No selected requirements</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div id="step1RemindersSummary" class="d-flex mt-1 justify-content-end" v-if="canShowSummary && selectedSummary.show">
      <div class="reminders-summary-wrapper">
        <AlertNotification typeAlert="success-soft" :showAlert="true" :showCloseButton="false">
          <p class="text-16 m-0">
            <strong>
              Reminders will be sent to {{ selectedSummary.totalEmployers }} different employers total for {{ selectedSummary.totalRequirements }} requirement<span v-if="selectedSummary.totalRequirements !== 1">s</span>.
            </strong>
          </p>
        </AlertNotification>
      </div>
    </div>

    <!-- Custom Date Selection Modal -->
    <BcsModal
      ref="customDateModalRef"
      modalSize="small"
      hideCloseButton
      :showFooter="true"
      @closeButtonClicked="onCustomDateCancel">
      <template #header>
        <div class="d-flex w-100 justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <i class="bi bi-alarm text-primary me-2 fs-5"></i>
            <span class="modal-title header-20">
              <strong>Add Due Date Reminders to External Calendar</strong>
            </span>
          </div>
          <button type="button" class="btn-close" @click="onCustomDateCancel" aria-label="Close"></button>
        </div>
      </template>
      
      <div class="text-center pt-2" v-if="selectedReqForCustomDate">
        <p class="header-20 mb-2">
          <strong>
            Custom Reminder Date Selected: {{ selectedCustomDate || '--' }}
          </strong>
        </p>
        <p class="text-16 mb-1">
          <strong>{{ selectedReqForCustomDate.requirementName }}</strong>
        </p>
        <p class="text-16 mb-3">
          Due Date: {{ formatDueDateForModal(selectedReqForCustomDate) }}
        </p>
        <div class="d-flex justify-content-center custom-datepicker-wrapper">
          <DatePicker
            v-model:selectedDate="selectedCustomDate"
            :dueDate="selectedReqForCustomDate.eventMonthDay"
            :contextualPlanYear="{ plan_start_date: selectedReqForCustomDate.employers?.[0]?.plan_start_date }" />
        </div>
      </div>
      
      <template #footer>
        <div class="d-flex justify-content-between w-100">
          <button type="button" class="btn btn-link text-primary text-decoration-none px-0 text-14" @click="onCustomDateCancel">
            <i class="bi bi-chevron-left"></i> <strong>Cancel and Go Back</strong>
          </button>
          <button type="button" class="btn btn-primary text-16" :disabled="!selectedCustomDate" @click="onCustomDateConfirm">Choose Custom Date</button>
        </div>
      </template>
    </BcsModal>
  </div>
</template>

<style scoped>
#sendRemindersRequirementsTable { border:1px solid #E5E7EB; border-collapse:collapse; border-spacing:0; }
#sendRemindersRequirementsTable thead th { border:none; border-bottom:1px solid #E5E7EB; }
#sendRemindersRequirementsTable tbody td { border:none; border-bottom:1px solid #E5E7EB; }
#sendRemindersRequirementsTable tbody tr:last-child td { border-bottom:none; }
.reminders-summary-wrapper { width: fit-content; }
.custom-datepicker-wrapper { margin: 0 auto; }
.sort-icon {
  padding-left: 5px;
  color: var(--bs-primary);
  cursor: pointer;
  background-color: var(--bs-info);
  border-radius: 4px;
}

/* Apply gray background to all cells when row is expanded */
#sendRemindersRequirementsTable tbody tr.bg-gray-100 > td {
  background-color: #f8f9fa;
}
</style>
